import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDivisionPositions, useLinkDivisionPosition, useUnlinkDivisionPosition,
  usePositions, useDeletePosition, errMessage, type Position,
} from './systemMasters';
import { PositionDetailDialog } from './PositionDetailDialog';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';

interface Props {
  companyId?: string;
  /** A not-yet-saved division: the position ids picked so far (applied on the division's Save). */
  staged?: string[];
  onStaged?: (ids: string[]) => void;
}

/**
 * Division detail ▸ Positions tab — the one-to-many dbo.Position.Company link.
 * Mirrors the legacy XAF Positions grid (PositionName / Sort / IsDivisionBased) + the
 * "Position" Link picker (checkbox list with search + per-row Edit pencil; OK syncs).
 */
export function DivisionPositionsPanel({ companyId, staged = [], onStaged }: Props) {
  // New division (no id yet): the tab works on the held list instead of the server (G5 parity, 2026-10-14).
  const staging = !companyId && !!onStaged;
  const gated = !companyId && !onStaged;
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: serverLinked = [] } = useDivisionPositions(companyId);
  const { data: all = [] } = usePositions();
  const link = useLinkDivisionPosition(companyId ?? '');
  const unlink = useUnlinkDivisionPosition(companyId ?? '');
  const del = useDeletePosition();
  const qc = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [pickOpen, setPickOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editPos, setEditPos] = useState<Position | null>(null);

  const linked: Position[] = useMemo(
    () => (staging ? all.filter((x) => staged.includes(x.id)) : serverLinked),
    [staging, all, staged, serverLinked],
  );
  const linkedIds = useMemo(() => new Set(linked.map((p) => p.id)), [linked]);

  const openPick = () => { setChecked(new Set(linkedIds)); setPickSearch(''); setPickOpen(true); };
  const pickRows = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return q ? all.filter((p) => (p.positionName ?? '').toLowerCase().includes(q)) : all;
  }, [all, pickSearch]);

  const applyPick = async () => {
    if (staging) { onStaged!(all.filter((x) => checked.has(x.id)).map((x) => x.id)); setPickOpen(false); return; }
    if (!companyId) return;
    setBusy(true);
    try {
      for (const p of all) {
        const isChecked = checked.has(p.id);
        const wasLinked = linkedIds.has(p.id);
        if (isChecked && !wasLinked) await link.mutateAsync(p.id);
        else if (!isChecked && wasLinked) await unlink.mutateAsync(p.id);
      }
      setPickOpen(false);
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one position to unlink.'); return; }
    if (!await confirm({ title: 'Unlink', message: `Remove ${selectedIds.size} position${selectedIds.size === 1 ? '' : 's'} from this division? (The position is kept.)` })) return;
    if (staging) { onStaged!(staged.filter((id) => !selectedIds.has(id))); setSelectedIds(new Set()); return; }
    try { for (const id of selectedIds) await unlink.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one position to edit.'); return; }
    const p = linked.find((x) => x.id === String([...selectedIds][0])); if (p) setEditPos(p);
  };
  // Delete the position ENTITY (soft-delete) — LIMS Delete on the nested grid.
  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one position to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} position${selectedIds.size === 1 ? '' : 's'}? This deletes the position itself.` })) return;
    if (staging) onStaged!(staged.filter((id) => !selectedIds.has(id)));
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); qc.invalidateQueries({ queryKey: ['sm-division-positions'] }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const cmp = (get: (r: Position) => string | null) => (a: Position, b: Position) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });
  const columns: AppColumn<Position>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (p) => <button className="pts-icon-btn edit" title="Edit position" onClick={(e) => { e.stopPropagation(); setEditPos(p); }}><i className="ti ti-edit" /></button>,
    },
    // LIMS sorts by the primary column only — ONLY PositionName carries the sort symbol; Sort / IsDivisionBased are filter-only.
    { field: 'positionName', header: 'PositionName', width: '34%', minWidth: 160, text: (r) => r.positionName ?? '', compare: cmp((r) => r.positionName) },
    { field: 'sort', header: 'Sort', width: '33%', minWidth: 80, align: 'right', type: 'number', sortable: false, text: (r) => (r.sort != null ? String(r.sort) : '') },
    {
      field: 'isDivisionBased', header: 'IsDivisionBased', width: '33%', minWidth: 130, sortable: false, align: 'center', text: (r) => (r.isDivisionBased ? 'Yes' : 'No'),
      render: (r) => <input type="checkbox" className="acc-grid-check" checked={r.isDivisionBased} readOnly />,
    },
  ];
  const pickCols: AppColumn<Position>[] = [
    {
      // Edit pencil leading (next to the checkbox), consistent with the grid.
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (p) => <button className="pts-icon-btn edit" title="Edit position" onClick={(e) => { e.stopPropagation(); setEditPos(p); }}><i className="ti ti-edit" /></button>,
    },
    { field: 'positionName', header: 'PositionName', minWidth: 360, text: (r) => r.positionName ?? '', compare: cmp((r) => r.positionName) },
  ];

  useHotkeys([
    { combo: 'alt+l', label: 'Link', group: 'Positions', order: 1, disabled: gated, run: openPick },
    { combo: 'alt+k', label: 'Unlink', group: 'Positions', order: 2, disabled: gated || selectedIds.size === 0, run: () => { void doUnlink(); } },
    { combo: 'alt+e', label: 'Edit', group: 'Positions', order: 3, disabled: gated || selectedIds.size !== 1, run: editSelected },
    { combo: 'alt+d', label: 'Delete', group: 'Positions', order: 4, disabled: gated || selectedIds.size === 0, run: () => { void doDelete(); } },
  ]);

  if (gated) return <div className="acc-tab-pane"><div style={{ padding: 14, color: '#8a7a66' }}>Save the division first to link positions.</div></div>;

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title={`Link (${formatCombo('alt+l')})`} onClick={openPick}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title={`Unlink (${formatCombo('alt+k')})`} onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title={`Edit (${formatCombo('alt+e')})`} onClick={editSelected} disabled={selectedIds.size !== 1}><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title={`Delete (${formatCombo('alt+d')})`} onClick={() => void doDelete()} disabled={selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="Division-Positions" headers={['PositionName', 'Sort', 'IsDivisionBased']}
          rows={linked.map((r) => [r.positionName ?? '', r.sort != null ? String(r.sort) : '', r.isDivisionBased ? 'Yes' : 'No'])} />
      </div>
      <AppDataGrid<Position> rows={linked} columns={columns} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={editPos?.id}
        emptyText="No positions linked."
        defaultPageSize={20} />

      {pickOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setPickOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick acc-dialog--accent" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Position</span><i className="ti ti-x acc-dialog-x" onClick={() => setPickOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input autoFocus placeholder="Search…" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} />
                {pickSearch && <i className="ti ti-x acc-search-x" onClick={() => setPickSearch('')} />}
              </div></div>
              <AppDataGrid<Position> rows={pickRows} columns={pickCols} getRowId={(r) => r.id}
                selectable selectedIds={checked} onSelectionChange={setChecked}
                initialSort={{ key: 'positionName', dir: 'asc' }} emptyText="No positions found."
                defaultPageSize={20} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyPick()} disabled={busy}>OK</button>
              <button className="ca-btn" onClick={() => setPickOpen(false)} disabled={busy}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editPos && <PositionDetailDialog position={editPos} onClose={() => setEditPos(null)} />}
    </div>
  );
}
